import { FC, type ReactNode } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import {
  MODAL_ICON_BADGE_SIZE,
  modalIconTint,
  type ModalIconTone,
} from '@components/ConfirmationModal';
import { XIcon } from '@phosphor-icons/react';

/**
 * The frame the remediation dialogs share: a titled panel with a subtitle and a
 * close affordance.
 *
 * Three of them had this header copied out — same `sx`, same two Typography
 * variants, same icon button — and had already drifted: one was missing the
 * `overflowWrap` the other two carried, so a long assessment name overflowed
 * there and nowhere else. That is the whole argument for a shell.
 *
 * Deliberately not `ConfirmationModal`: that answers a yes/no question and owns
 * its own buttons. These present a panel of content the user reads or acts on.
 * It does, however, LOOK like one — same 3-step corner radius, same circular
 * tinted icon badge from the same `modalIconTint` map, same rules above and
 * below the body, same footer metrics. A user should not be able to tell that a
 * remediation panel and a confirmation come from different components, and
 * before this they could: these had square-ish default corners, no badge, and
 * whatever padding each caller's own `DialogActions` happened to set.
 */
export const RemediationDialog: FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  /** Second line — an assessment name, or a sentence of context. */
  subtitle?: ReactNode;
  /**
   * Glyph for the header badge. Every app modal leads with one; omit only when
   * there is genuinely nothing to say about what kind of panel this is.
   */
  icon?: ReactNode;
  /** Badge tone. Defaults to `primary`, the neutral "here is a panel" blue. */
  iconTone?: ModalIconTone;
  /**
   * Widen past the default. Panels that hold a table or a run history need more
   * room than a confirmation does; `sm` matches `ConfirmationModal`'s width.
   */
  maxWidth?: 'sm' | 'md';
  children: ReactNode;
  /**
   * Footer content — buttons, or a summary line beside them. Rendered inside a
   * `DialogActions` the shell styles, so callers no longer set their own padding
   * and the footers stop disagreeing about it. Omitted, there is no footer and no
   * rule above it.
   */
  actions?: ReactNode;
}> = observer(
  ({
    open,
    onClose,
    title,
    subtitle,
    icon,
    iconTone = 'primary',
    maxWidth = 'sm',
    children,
    actions,
  }) => {
    const theme = useTheme();
    const tint = modalIconTint(theme, iconTone);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, overflow: 'hidden' } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: MODAL_ICON_BADGE_SIZE,
                  height: MODAL_ICON_BADGE_SIZE,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: tint.bg,
                  color: tint.color,
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {/* `h6`/600, as `ConfirmationModal` sets it — these used to be
                  `textLG_semiBold`, which renders a shade lighter and made the
                  two kinds of dialog read as different headers. */}
              <Typography variant="h6" component="div" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant="textSM_regular"
                  // `anywhere`, because a subtitle is usually a document or
                  // assessment name and those are neither short nor guaranteed to
                  // contain spaces.
                  sx={{ color: 'text.secondary', display: 'block', overflowWrap: 'anywhere' }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
            <XIcon size={20} />
          </IconButton>
        </DialogTitle>

        <Divider />

        {/* The body scrolls between the two rules, which is what the rules are
            for — a panel's content is a list or a table and runs past the fold
            far more often than a confirmation's sentence does. */}
        <DialogContent sx={{ pt: 2.5, pb: 2 }}>{children}</DialogContent>

        {actions ? (
          <>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>{actions}</DialogActions>
          </>
        ) : null}
      </Dialog>
    );
  },
);
